/*
 * Copyright © 2024 Himitsu Lab Limited. All Rights Reserved.
 */

import { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { getCognitoObject, getIsSignUpTermsAccepted } from "../../Services/signUpReducer"
import { getCurrentUser } from "../../Services/userReducer"
import { useAppSelector, useAppDispatch } from "../../Store/hooks"
import { User, Verified } from "../../models/user.model"
import { getToken } from "../../Services/tokenReducer"
import { getIsSkipping } from "../../Services/navigationReducer"
import { userApi } from "../../Services/userApi"

/**
 * This hook is used to manage the routing of the application.
 * When a user logs in, the hook checks if the user has accepted the terms and conditions,
 * if the user has verified their email, if the user has verified their phone,
 * if the user has completed their face verification, and if the user has completed their profile.
 * If any of these conditions are not met, the user is redirected to the appropriate page.
 * @return {object} Object containing loading state.
 */
export const useAppRouting = () => {
  const [isLoading, setIsLoading] = useState(true)
  const [initialDataFetched, setInitialDataFetched] = useState(false)
  const dispatch = useAppDispatch()
  const currentUser: User = useSelector(getCurrentUser)
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const userAccessToken = useAppSelector(getToken)

  const termsStatus = useAppSelector(getIsSignUpTermsAccepted)
  const cognitoModel = useAppSelector(getCognitoObject) ?? JSON.parse(localStorage.getItem('SIGNIN_HELPER_COGNITOMODEL') as any)
  const isSkipping = useAppSelector(getIsSkipping)

  const excludedPaths = [
    '/signUp',
    '/signIn',
    '/termsAndConditions',
    '/about',
    '/faq',
    '/intro/choosePreference',
    '/verifyEmail',
    '/verifyPhone',
    '/verifyFace',
  ]
  
  const isExcluded =
    excludedPaths.includes(pathname) ||
    pathname.startsWith('/resetPassword') ||
    pathname.startsWith('/loginSuccessful')
  // Load user data only once when component mounts or token changes
  useEffect(() => {
    const loadUserData = async () => {
      // Skip if we've already fetched data with this token
      if (initialDataFetched && userAccessToken) {
        return
      }
      
      setIsLoading(true)
      
      if (userAccessToken) {
        try {
          // Fetch fresh user data to ensure routing decisions are based on latest data
          await dispatch(
            userApi.endpoints.getCurrentUserDetails.initiate(undefined, { forceRefetch: true })
          ).unwrap()
          
          // Mark that we've fetched data for this token
          setInitialDataFetched(true)
        } catch (error) {
          console.error("Failed to load user details:", error)
        }
      }
      
      setIsLoading(false)
    }

    loadUserData()
  }, [dispatch, userAccessToken, initialDataFetched])
  
  // Reset initialDataFetched when token changes
  useEffect(() => {
    if (!userAccessToken) {
      setInitialDataFetched(false)
    }
  }, [userAccessToken])

  // Handle routing logic only after loading is complete
  useEffect(() => {
    // Don't make routing decisions while still loading user data
    if (isLoading) {
      return
    }

    if (currentUser && userAccessToken) {
      const needsToAcceptTerms =
        (!currentUser.isUserTermsPolicyStatus && !termsStatus) &&
        pathname.startsWith('/s/') &&
        !isExcluded

      if (needsToAcceptTerms) {
        navigate('/termsAndConditions')
        return
      }

      if (isSkipping) {
        return
      }
      
      if (currentUser.emailVerified === Verified.NotComplete) {
        if (pathname !== '/verifyEmail') {
          navigate('/verifyEmail')
          return
        }
      } else if (
        ((currentUser.phoneVerified === Verified.Complete && currentUser.emailVerified === Verified.Complete) && (pathname === '/verifyEmail')) || 
        ((currentUser.emailVerified === Verified.Complete && currentUser.phoneVerified === Verified.Complete) && (pathname === '/verifyPhone'))
      ) {
        navigate('/intro/choosePreference')
        return
      } else if (currentUser.phoneVerified === Verified.NotComplete && pathname !== '/verifyPhone') {
        navigate('/verifyPhone')
        return
      } else if (currentUser.faceVerified === Verified.NotComplete && pathname !== '/verifyFace' && currentUser.phoneVerified === Verified.Complete) {
        navigate('/verifyFace')
        return
      }
      // else if (currentUser.userCategory?.length === 0 && (currentUser.faceVerified === Verified.Complete || currentUser.faceVerified === Verified.Skipped)) {
      //   if (pathname !== '/s/chooseInterests') {
      //     navigate('/s/chooseInterests')
      //     return
      //   }
      // }

    } else if (cognitoModel) {
      if (pathname !== '/verifyPhone') {
        navigate('/verifyPhone')
        return
      }
    } else if (!userAccessToken || !currentUser) {
      if (
        pathname !== '/signUp' && 
        pathname !== '/signIn' && 
        pathname !== '/termsAndConditions' && 
        !pathname.startsWith('/resetPassword') && 
        pathname !== '/about' && 
        pathname !== '/faq' && 
        !pathname.startsWith('/loginSuccessful') && 
        termsStatus
      ) {
        navigate('/signUp')
        return
      }
      // else if (pathname !== '/termsAndConditions' && pathname !== '/signIn' && pathname !== '/signUp' && pathname !== '/' && !termsStatus && !pathname.startsWith('/resetPassword') && pathname !== '/about' && pathname !== '/faq' && !pathname.startsWith('/loginSuccessful')) {
      //   navigate('/termsAndConditions')
      //   return
      // }
    }
  }, [currentUser, userAccessToken, termsStatus, navigate, cognitoModel, isSkipping, pathname, isExcluded, isLoading])

  return {}
}